import Transclude, { TranscludeProps } from "./Transclude";
import React from "react";

type ExcerptProps = Pick<TranscludeProps, "emphasize">;
const excerpt = (
  src: TranscludeProps["src"],
  focusOn: TranscludeProps["focusOn"],
  extra: {
    transform?: TranscludeProps["transform"];
    insert?: TranscludeProps["insert"];
  } = {},
) => {
  const Excerpt: React.FC<ExcerptProps> = ({ emphasize }) => (
    <Transclude
      src={src}
      focusOn={focusOn}
      emphasize={emphasize}
      transform={extra.transform}
      insert={extra.insert}
      copyAllCode={false}
    />
  );
  return Excerpt;
};

// ----------

import allocString from "rust:extracted/alloc/src/string.rs";

export const String = excerpt(allocString, "353+3");
export const StringFromUtf8 = excerpt(allocString, "415,560,565,2206", {
  insert: { 3: "        // ..." },
});

// ----------

import coreFutureFuture from "rust:extracted/core/src/future/future.rs";

export const FutureTrait = excerpt(coreFutureFuture, "37,41+2,113+2");

// ----------

import coreIterTraitsIterator from "rust:extracted/core/src/iter/traits/iterator.rs";

export const IteratorTrait = excerpt(coreIterTraitsIterator, "40,44+2,76,4037");

export const IteratorByRef = excerpt(coreIterTraitsIterator, "40,1896+3,4037");
export const IteratorCollect = excerpt(
  coreIterTraitsIterator,
  "40,2015+3,4037",
);
export const IteratorCycle = excerpt(coreIterTraitsIterator, "40,3497+3,4037");
export const IteratorFilter = excerpt(coreIterTraitsIterator, "40,893+4,4037");
export const IteratorMap = excerpt(coreIterTraitsIterator, "40,773+4,4037");
export const IteratorRev = excerpt(coreIterTraitsIterator, "40,3351+3,4037");
export const IteratorRposition = excerpt(
  coreIterTraitsIterator,
  "40,3114+4,4037",
);

// ----------

import coreIterTraitsCollect from "rust:extracted/core/src/iter/traits/collect.rs";

export const IntoIteratorTrait = excerpt(
  coreIterTraitsCollect,
  "282,285,289+2,310+2",
);

// ----------

import coreOption from "rust:extracted/core/src/option.rs";

export const OptionEnum = excerpt(coreOption, "600,604,608+2", {
  transform: { 3: [/#\[.*\] /, ""] },
});

// ----------

import coreSliceMod from "rust:extracted/core/src/slice/mod.rs";

export const SliceSplitAtMut = excerpt(coreSliceMod, "1983+6");
export const SliceSplitAtMutChecked = excerpt(coreSliceMod, "2189+2,2193+5");
export const SliceSplitAtMutUnchecked = excerpt(coreSliceMod, "2089+9,2103+7");

// ----------

import coreSliceRaw from "rust:extracted/core/src/slice/raw.rs";

export const SliceFromRawParts = excerpt(coreSliceRaw, "124,141", {
  insert: { 2: "    // ..." },
});
export const SliceFromRawPartsMut = excerpt(coreSliceRaw, "179,196", {
  insert: { 2: "    // ..." },
});

// ----------

import coreStrConverts from "rust:extracted/core/src/str/converts.rs";

export const StrFromUtf8 = excerpt(coreStrConverts, "89,98", {
  insert: { 2: "    // ..." },
});

// ----------

import coreStrIter from "rust:extracted/core/src/str/iter.rs";

export const CharsIterator = excerpt(coreStrIter, "138,159,162+2", {
  insert: { 3: "        // ..." },
});

// ----------

import coreTaskPoll from "rust:extracted/core/src/task/poll.rs";

export const PollEnum = excerpt(coreTaskPoll, "14,18,27+2", {
  transform: { 2: [/#\[.*\] /, ""] },
});

// ----------

import stdThreadFunctions from "rust:extracted/std/src/thread/functions.rs";

export const ThreadSpawn = excerpt(stdThreadFunctions, "125+5");

// ----------

import stdThreadScoped from "rust:extracted/std/src/thread/scoped.rs";

export const ThreadScopedScope = excerpt(stdThreadScoped, "138-140");
export const ThreadScopedSpawn = excerpt(stdThreadScoped, "173,198-201,205");

// ----------

import futuresCoreFuture from "rust:extracted/futures-core-0.3.32/src/future.rs";

export const TryFutureTrait = excerpt(futuresCoreFuture, "68,70,73+2,80+2");

// ----------

import futuresCoreStream from "rust:extracted/futures-core-0.3.32/src/stream.rs";

export const StreamTrait = excerpt(futuresCoreStream, "37,39+2,76,108");
export const TryStreamTrait = excerpt(futuresCoreStream, "177,179,182+2,189+5");

// ----------

import futuresIo from "rust:extracted/futures-io-0.3.32/src/lib.rs";

export const FuturesIoAsyncReadPollRead = excerpt(futuresIo, "63+5");

// ----------

import futuresUtilIoMod from "rust:extracted/futures-util-0.3.32/src/io/mod.rs";

export const FuturesAsyncReadExtRead = excerpt(futuresUtilIoMod, "196+3");

// ----------

import futuresUtilFutureFutureMod from "rust:extracted/futures-util-0.3.32/src/future/future/mod.rs";

export const FutureExtTrait = excerpt(futuresUtilFutureFutureMod, "121,603", {
  insert: { 2: "    // 19 methods" },
});

// ----------

import futuresUtilFutureTryFutureMod from "rust:extracted/futures-util-0.3.32/src/future/try_future/mod.rs";

export const TryFutureExtTrait = excerpt(
  futuresUtilFutureTryFutureMod,
  "137,625",
  {
    insert: { 2: "    // 16 methods" },
  },
);

// ----------

import futuresUtilStreamStreamMod from "rust:extracted/futures-util-0.3.32/src/stream/stream/mod.rs";

export const StreamExtTrait = excerpt(futuresUtilStreamStreamMod, "249,1697", {
  insert: { 2: "    // 46 methods" },
});

// ----------

import futuresUtilStreamTryStreamMod from "rust:extracted/futures-util-0.3.32/src/stream/try_stream/mod.rs";

export const TryStreamExtTrait = excerpt(
  futuresUtilStreamTryStreamMod,
  "180,1254",
  {
    insert: { 2: "    // 29 methods" },
  },
);

// ----------

import synDerive from "rust:extracted/syn-2.0.117/src/derive.rs";

export const DeriveInput = excerpt(synDerive, "13-19");
export const Data = excerpt(synDerive, "31-35");
export const DataStruct = excerpt(synDerive, "41-45");

// ----------

import tokioIoAsyncRead from "rust:extracted/tokio-1.50.0/src/io/async_read.rs";

export const TokioAsyncReadPollRead = excerpt(tokioIoAsyncRead, "55+5");

// ----------

import tokioIoUtilAsyncReadExt from "rust:extracted/tokio-1.50.0/src/io/util/async_read_ext.rs";

export const TokioAsyncReadExtRead = excerpt(tokioIoUtilAsyncReadExt, "177+3");
